import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BaseCodeSelectbox from '../common/form/selectbox/BaseCodeSelectbox';
import DetailTemplate from '../common/template/DetailTemplate';
import PartnerModalConatiner from '../../containers/common/form/modal/PartnerModalConatiner';
import FormLabel from '../common/form/FormLabel';
import * as partnerApi from '../../lib/api/partner';
import { subscribePartnerCreated } from '../../utils/partnerCreatedEvent';

const ProjectForm = ({ formik, isFieldEnabled, isFieldRequired }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const partnerReturnTokenRef = useRef(
    `project-form-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );
  
  // 담당자 관련 상태
  const [contactList, setContactList] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const selectedCustomerUuid = formik.values.customerUuid || formik.values.vendorCmp || '';
  
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  // 고객 선택 시 customerUuid 저장 + 담당자 목록 조회
  const selectedPartner = useCallback((partner) => {
    const partnerUuid = partner?.partnerUuid || '';
    formik.setFieldValue('partnerInc', partner?.partnerNm || '');
    formik.setFieldValue('customerUuid', partnerUuid);
    formik.setFieldValue('vendorCmp', partnerUuid);
    
    // 담당자 정보 초기화
    formik.setFieldValue('chargeNm', '');
    formik.setFieldValue('chargeEml', '');
    formik.setFieldValue('chargeTel', '');
    formik.setFieldValue('vendorEmp', '');
    setSelectedContact(null);
    setContactList([]);
  }, [formik]);

  const handlePartnerIncChange = (event) => {
    formik.handleChange(event);

    if (selectedCustomerUuid) {
      formik.setFieldValue('customerUuid', '');
      formik.setFieldValue('vendorCmp', '');
      formik.setFieldValue('chargeNm', '');
      formik.setFieldValue('chargeEml', '');
      formik.setFieldValue('chargeTel', '');
      formik.setFieldValue('vendorEmp', '');
      setSelectedContact(null);
      setContactList([]);
    }
  };

  const handlePartnerIncBlur = async (event) => {
    formik.handleBlur(event);
    const partnerName = event.target.value?.trim();

    if (!partnerName || selectedCustomerUuid) {
      return;
    }

    try {
      const res = await partnerApi.getPartners({ partnerNm: partnerName });
      const partners = res.data?.list || [];
      const normalizedName = normalizePartnerName(partnerName);
      const exactMatch = partners.find(
        (partner) => normalizePartnerName(partner.partnerNm) === normalizedName,
      );
      const matchedPartner = exactMatch || (partners.length === 1 ? partners[0] : null);

      if (matchedPartner?.partnerUuid) {
        selectedPartner(matchedPartner);
      }
    } catch (err) {
      console.error('고객명 거래처 조회 실패', err);
    }
  };

  const handlePartnerRegisterOpen = () => {
    const params = new URLSearchParams({
      returnTo: 'projectForm',
      returnToken: partnerReturnTokenRef.current,
    });
    window.open(`/partnermng/add?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  // 담당자 목록 조회
  const fetchPartnerContacts = async (partnerUuid) => {
    if (!partnerUuid) {
      setContactList([]);
      return;
    }
    setLoadingContacts(true);
    try {
      const res = await partnerApi.getPartnerEmp(partnerUuid);
      setContactList(res.data?.list || []);
    } catch (err) {
      console.error('담당자 조회 실패', err);
      setContactList([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const normalizePartnerName = (value) => {
    return (value || '').replace(/\s+/g, '').toLowerCase();
  };

  // 담당자 선택 시 정보 자동 채움
  const handleContactSelect = (event, contact) => {
    setSelectedContact(contact);
    if (contact) {
      formik.setFieldValue('chargeNm', contact.employeeNm || '');
      formik.setFieldValue('chargeEml', contact.emailAddr || '');
      formik.setFieldValue('chargeTel', contact.telNo || '');
      formik.setFieldValue('vendorEmp', contact.employeeUuid || '');
    } else {
      formik.setFieldValue('chargeNm', '');
      formik.setFieldValue('chargeEml', '');
      formik.setFieldValue('chargeTel', '');
      formik.setFieldValue('vendorEmp', '');
    }
  };

  // 고객사가 정해지면 담당자 목록 조회
  useEffect(() => {
    if (selectedCustomerUuid) {
      fetchPartnerContacts(selectedCustomerUuid);
    } else {
      setContactList([]);
    }
  }, [selectedCustomerUuid]);

  useEffect(() => {
    return subscribePartnerCreated((event) => {
      if (
        event.returnTo !== 'projectForm' ||
        event.returnToken !== partnerReturnTokenRef.current ||
        !event.partner?.partnerUuid
      ) {
        return;
      }

      selectedPartner(event.partner);
    });
  }, [selectedPartner]);

  // 필수 필드 마크
  const requiredMark = (
    <Typography
      variant="subtitle2"
      component="span"
      sx={{ ml: 0.5, color: 'error.main' }}
    >
      *
    </Typography>
  );

  // 필드가 활성화되어 있고 필수인지 확인하여 마크 표시
  const getRequiredMark = (fieldCd) => {
    return isFieldRequired(fieldCd) ? requiredMark : null;
  };

  return (
    <div>
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pb: { xs: 8, sm: 0 },
        }}
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <DetailTemplate title="기회 확인">
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              justifyContent: 'flex-end',
              alignItems: 'center',
              my: 2,
            }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting}
              sx={{ minWidth: '80px' }}
            >
              저장
            </Button>
          </Box>

          {/* 모바일(xs)에서는 상단 단독 저장 버튼 대신 화면 하단 고정 저장바를 사용한다 */}
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: (theme) => theme.zIndex.appBar,
              justifyContent: 'center',
              alignItems: 'center',
              px: 2,
              py: 1,
              pb: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
              backgroundColor: 'background.paper',
              borderTop: '1px solid #e0e0e0',
              boxShadow: '0 -2px 6px rgba(0,0,0,0.08)',
            }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting}
              fullWidth
              sx={{ maxWidth: 400 }}
            >
              저장
            </Button>
          </Box>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>프로젝트 기본 정보</Typography>
            
            {/* 
              CSS Grid 사용 (Material-UI Grid 대신)
              이유: 
              1. 필드별 최대 너비 제한 (300px ~ 500px)
              2. 자동으로 빈 공간 채우기 (auto-fit)
              3. 필드 개수에 관계없이 균등한 배치
            */}
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 500px))',
              gap: 2,
              mb: 2
            }}>
              {isFieldEnabled('prjTyp') && (
                <Box>
                  <FormLabel>프로젝트 타입{getRequiredMark('prjTyp')}</FormLabel>
                  <BaseCodeSelectbox
                    formik={formik}
                    filterObject={{
                      blcCd: 'BASE',
                      mlcCd: 'FORCAST',
                      slcCd: 'PRJ_TYP',
                      useYn: 'Y',
                    }}
                    name="prjTyp"
                    size="small"
                    required={isFieldRequired('prjTyp')}
                    fullWidth
                  />
                </Box>
              )}
              {isFieldEnabled('prjImpt') && (
                <Box>
                  <FormLabel>프로젝트 중요도{getRequiredMark('prjImpt')}</FormLabel>
                  <BaseCodeSelectbox
                    formik={formik}
                    filterObject={{
                      blcCd: 'BASE',
                      mlcCd: 'FORCAST',
                      slcCd: 'PRJ_IMPT',
                      useYn: 'Y',
                    }}
                    name="prjImpt"
                    size="small"
                    required={isFieldRequired('prjImpt')}
                    fullWidth
                  />
                </Box>
              )}
              {isFieldEnabled('bugetYn') && (
                <Box>
                  <FormLabel>예산여부{getRequiredMark('bugetYn')}</FormLabel>
                  <BaseCodeSelectbox
                    formik={formik}
                    filterObject={{
                      blcCd: 'BASE',
                      mlcCd: 'FORCAST',
                      slcCd: 'BUGET_YN',
                      useYn: 'Y',
                    }}
                    name="bugetYn"
                    size="small"
                    required={isFieldRequired('bugetYn')}
                    fullWidth
                  />
                </Box>
              )}
              {isFieldEnabled('chanceTyp') && (
                <Box>
                  <FormLabel>기회 구분{getRequiredMark('chanceTyp')}</FormLabel>
                  <BaseCodeSelectbox
                    formik={formik}
                    filterObject={{
                      blcCd: 'BASE',
                      mlcCd: 'FORCAST',
                      slcCd: 'CHANCE_TYP',
                      useYn: 'Y',
                    }}
                    name="chanceTyp"
                    size="small"
                    required={isFieldRequired('chanceTyp')}
                    fullWidth
                  />
                </Box>
              )}
              {isFieldEnabled('partnerInc') && (
                <Box>
                  <FormLabel>고객명{getRequiredMark('partnerInc')}</FormLabel>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      required={isFieldRequired('partnerInc')}
                      variant="outlined"
                      fullWidth
                      id="partnerInc"
                      name="partnerInc"
                      value={formik.values.partnerInc}
                      onChange={handlePartnerIncChange}
                      onBlur={handlePartnerIncBlur}
                      error={
                        formik.touched.partnerInc &&
                        Boolean(formik.errors.partnerInc)
                      }
                      helperText={
                        formik.touched.partnerInc && formik.errors.partnerInc
                      }
                    />
                    <Button
                      color="secondary"
                      variant="outlined"
                      size="small"
                      onClick={handleOpen}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      고객 선택
                    </Button>
                    <Button
                      color="warning"
                      variant="outlined"
                      size="small"
                      type="button"
                      onClick={handlePartnerRegisterOpen}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      거래처 등록
                    </Button>
                  </Box>
                </Box>
              )}
              {isFieldEnabled('incTyp') && (
                <Box>
                  <FormLabel>거래처 유형{getRequiredMark('incTyp')}</FormLabel>
                  <BaseCodeSelectbox
                    formik={formik}
                    filterObject={{
                      blcCd: 'BASE',
                      mlcCd: 'FORCAST',
                      slcCd: 'INC_TYP',
                      useYn: 'Y',
                    }}
                    name="incTyp"
                    size="small"
                    required={isFieldRequired('incTyp')}
                    fullWidth
                  />
                </Box>
              )}
              {isFieldEnabled('cstNm') && (
                <Box sx={{ gridColumn: '1 / -1' }}> {/* 전체 너비 사용 */}
                  <FormLabel>사업명{getRequiredMark('cstNm')}</FormLabel>
                  <TextField
                    size="small"
                    required={isFieldRequired('cstNm')}
                    variant="outlined"
                    fullWidth
                    id="cstNm"
                    name="cstNm"
                    value={formik.values.cstNm}
                    onChange={formik.handleChange}
                    error={formik.touched.cstNm && Boolean(formik.errors.cstNm)}
                    helperText={formik.touched.cstNm && formik.errors.cstNm}
                  />
                </Box>
              )}
              {isFieldEnabled('solutionTyp') && (
                <Box>
                  <FormLabel>아이템 구분{getRequiredMark('solutionTyp')}</FormLabel>
                  <BaseCodeSelectbox
                    formik={formik}
                    filterObject={{
                      blcCd: 'BASE',
                      mlcCd: 'FORCAST',
                      slcCd: 'SOL_TYPE',
                      useYn: 'Y',
                    }}
                    name="solutionTyp"
                    size="small"
                    required={isFieldRequired('solutionTyp')}
                    fullWidth
                  />
                </Box>
              )}
            </Box>
          </Paper>

          {/* 고객 담당자 정보 - 관련 필드 중 하나라도 활성화되어 있으면 표시 */}
          {(isFieldEnabled('chargeNm') || isFieldEnabled('chargeEml') || isFieldEnabled('chargeTel')) && (
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>고객 담당자 정보</Typography>
              
              {/* CSS Grid - 위와 동일한 이유 */}
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 500px))',
                gap: 2
              }}>
                {/* 담당자 선택 Autocomplete */}
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <FormLabel>담당자 선택</FormLabel>
                  <Autocomplete
                    size="small"
                    options={contactList}
                    getOptionLabel={(option) => 
                      option ? `${option.employeeNm || ''}${option.dutiesNm ? ` (${option.dutiesNm})` : ''}` : ''
                    }
                    value={selectedContact}
                    onChange={handleContactSelect}
                    loading={loadingContacts}
                    disabled={!selectedCustomerUuid}
                    noOptionsText={selectedCustomerUuid ? "등록된 담당자가 없습니다" : "고객 선택 버튼으로 거래처를 먼저 선택하세요"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={selectedCustomerUuid ? "담당자를 선택하세요" : "고객 선택 버튼으로 거래처를 먼저 선택하세요"}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option?.employeeUuid === value?.employeeUuid}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    담당자를 선택하면 아래 정보가 자동으로 입력됩니다. 직접 입력도 가능합니다.
                  </Typography>
                </Box>
                
                {isFieldEnabled('chargeNm') && (
                  <Box>
                    <FormLabel>고객 담당자{getRequiredMark('chargeNm')}</FormLabel>
                    <TextField
                      size="small"
                      required={isFieldRequired('chargeNm')}
                      variant="outlined"
                      fullWidth
                      id="chargeNm"
                      name="chargeNm"
                      value={formik.values.chargeNm}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.chargeNm && Boolean(formik.errors.chargeNm)
                      }
                      helperText={formik.touched.chargeNm && formik.errors.chargeNm}
                    />
                  </Box>
                )}
                {isFieldEnabled('chargeEml') && (
                  <Box>
                    <FormLabel>이메일{getRequiredMark('chargeEml')}</FormLabel>
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      id="chargeEml"
                      name="chargeEml"
                      value={formik.values.chargeEml}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.chargeEml && Boolean(formik.errors.chargeEml)
                      }
                      helperText={
                        formik.touched.chargeEml && formik.errors.chargeEml
                      }
                    />
                  </Box>
                )}
                {isFieldEnabled('chargeTel') && (
                  <Box>
                    <FormLabel>연락처{getRequiredMark('chargeTel')}</FormLabel>
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      id="chargeTel"
                      name="chargeTel"
                      value={formik.values.chargeTel}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.chargeTel && Boolean(formik.errors.chargeTel)
                      }
                      helperText={
                        formik.touched.chargeTel && formik.errors.chargeTel
                      }
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          )}

          {/* 추가 정보 */}
          {isFieldEnabled('forcastTxt') && (
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>추가 정보</Typography>
              
              {/* 참고사항은 다중 라인 텍스트이므로 전체 너비 사용 */}
              <Box>
                <FormLabel>참고사항{getRequiredMark('forcastTxt')}</FormLabel>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  id="forcastTxt"
                  name="forcastTxt"
                  multiline
                  rows={3}
                  value={formik.values.forcastTxt}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.forcastTxt &&
                    Boolean(formik.errors.forcastTxt)
                  }
                  helperText={
                    formik.touched.forcastTxt && formik.errors.forcastTxt
                  }
                />
              </Box>
            </Paper>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mt: 3,
              gap: 2
            }}
          >
            <Button
              type="button"
              variant="outlined"
              disabled={formik.isSubmitting}
              sx={{ minWidth: '80px' }}
              onClick={() => navigate(-1)}
            >
              취소
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={formik.isSubmitting}
              sx={{ minWidth: '80px' }}
            >
              저장
            </Button>
          </Box>
        </DetailTemplate>
      </Box>
      {open && (
        <PartnerModalConatiner
          link
          title="고객명 선택"
          open={open}
          handleClose={handleClose}
          selectedPartner={selectedPartner}
        />
      )}
    </div>
  );
};

export default ProjectForm;
